// @ts-check

import frel from '../assets/frel.js'

/**
 * Custom element for handling the click and collect form.
 *
 * @class
 * @extends HTMLElement
 */
class ProductClickAndCollectCustom extends HTMLElement {
  constructor() {
    super()

    /** @type {HTMLElement | null} The container for the date select element */
    this.dateSelectContainer = null

    /** @type {boolean} Indicates if the click and collect form is enabled */
    this.enabled = false

    /** @type {HTMLElement | null} The details element for the click and collect form */
    this.details = this.querySelector('[data-component-click-and-collect-custom]')

    /** @type {HTMLInputElement | null} The input element for the store address */
    this.storeAddressInput = null

    /** @type {number | null} The ID of the store that should be selected by default */
    this.selectedStoreId = null

    /** @type {number} The index of the input that should be selected by default */
    this.selectedStoreInputIndex = 0

    /** @type {HTMLSelectElement[]} The array of date select elements */
    this.dateSelectElements = []

    /** @type {HTMLSelectElement | null} The select element for the dates */
    this.dateSelectElement = null

    /** @type {HTMLElement | null} The container for the store select elements */
    this.storeSelectContainer = null

    /** @type {HTMLElement[]} The array of custom select elements that are connected to the native select element */
    this.customSelectElements = []

    /** @type {SlimSelect | null} */
    this.storeSlimSelect = null

    /** @type {ProductDeliveryOptionsCustom | null} Reference to the product-delivery-options-custom element */
    this.productDeliveryOptionsCustom = null

    /** @type {InStoreAccordion | null} Reference to the in-store-accordion element */
    this.inStoreAccordion = null

    /** @type {ProductFormCustom | null} Reference to the product-form-custom element */
    this.productFormCustom = null

    /** @type {boolean} Indicates if all mandatory options are selected */
    this.allMandatoryOptionsSelected = false
  }

  connectedCallback() {
    this.setHtmlElements()
    this.setListeners()
    this.initializeDateSlimSelect()
  }

  initializeDateSlimSelect() {
    // The first time this is called, SlimSelect is not loaded, we rely connected callback being called again later anyways, should fix someday.
    if (typeof SlimSelect !== 'undefined') {
      this.dateSlimSelect = new SlimSelect({
        select: this.dateSelectElement,
        settings: {
          showSearch: false,
          placeholderText: 'Select a date',
          allowDeselect: true,
        },
      })
    }
  }

  /**
   * Sets the selected store by its index in the list.
   * @param {number} index - The index of the store to select.
   */
  setSelectedStoreInputIndex(index) {
    this.selectedStoreInputIndex = index
    // Update the checked state of inputs
    const inputs = this.storeSelectContainer.querySelectorAll('input[type="radio"]')
    inputs.forEach((input, idx) => {
      if (idx === index) {
        input.checked = true
        input.dispatchEvent(new Event('change', { bubbles: true }))
      } else {
        input.checked = false
      }
    })
  }

  /**
   * Sets the selected store by its ID.
   * @param {number} storeId - The ID of the store to select.
   */
  setSelectedStoreById(storeId) {
    this.selectedStoreId = storeId
    // Update the checked state of inputs
    const inputs = this.storeSelectContainer.querySelectorAll('input[type="radio"]')
    let found = false
    inputs.forEach((input) => {
      if (parseInt(input.dataset.retailLocationId, 10) === storeId) {
        input.checked = true
        input.dispatchEvent(new Event('change', { bubbles: true }))
        found = true
      } else {
        input.checked = false
      }
    })
    if (!found) {
      console.warn(`Store with ID ${storeId} not found.`)
    }
  }

  /**
   * Updates the store address input value.
   * @param {string} storeAddress - The address of the selected store.
   */
  updateStoreAddress(storeAddress) {
    if (this.storeAddressInput instanceof HTMLInputElement) {
      this.storeAddressInput.value = storeAddress
    }
  }

  handleDetailsToggle() {
    if (this.details.open) {
      this.productDeliveryOptionsCustom.close()
      this.inStoreAccordion.close()
    }
    this.productFormCustom.checkIfAddToCartShouldBeEnabled()
  }

  handleDateSelectInputChange() {
    this.dataset.showMandatoryFieldsWarning = 'false'
  }

  setListeners() {
    this.details.addEventListener('toggle', this.handleDetailsToggle.bind(this))

    if (this.dateSelectElement)
      this.dateSelectElement.addEventListener('change', this.handleDateSelectInputChange.bind(this))
  }

  /**
   * Checks if the mandatory date field is selected and updates data attributes accordingly.
   * This method can be called externally.
   */
  checkIfAllMandatoryOptionsSelected() {
    const dateValue = this.dateSelectElement.value
    if (dateValue === '') {
      this.allMandatoryOptionsSelected = false
      this.dataset.allMandatoryOptionsSelected = 'false'
      this.dataset.showMandatoryFieldsWarning = 'true'
    } else {
      this.allMandatoryOptionsSelected = true
      this.dataset.allMandatoryOptionsSelected = 'true'
      this.dataset.showMandatoryFieldsWarning = 'false'
    }
  }

  updateAvailability() {
    if (this.enabled) {
      this.details.setAttribute('open', '')
      this.dataset.enabled = true
    } else {
      this.close()
      this.dataset.enabled = false
    }
  }

  disable() {
    this.enabled = false
    this.updateAvailability()
  }

  close() {
    this.details.removeAttribute('open')
  }

  setDateSelectElement(dateSelectElement) {
    this.dateSelectElement = dateSelectElement
  }

  setStoreSelectContainer(storeSelectContainer) {
    this.storeSelectContainer = storeSelectContainer
  }

  setProductDeliveryOptionsCustom(productDeliveryOptionsCustom) {
    this.productDeliveryOptionsCustom = productDeliveryOptionsCustom
  }

  setInStoreAccordion(inStoreAccordion) {
    this.inStoreAccordion = inStoreAccordion
  }

  setProductFormCustom(productFormCustom) {
    this.productFormCustom = productFormCustom
  }

  setStoreAddressInput(storeAddressInput) {
    this.storeAddressInput = storeAddressInput
  }

  setHtmlElements() {
    const dateSelectElement = this.querySelector('[data-component-click-and-collect-delivery-date-select]')
    if (dateSelectElement instanceof HTMLSelectElement) {
      this.setDateSelectElement(dateSelectElement)
    }

    const storeSelectContainer = this.querySelector('[data-component-click-and-collect-store-select-container]')
    if (storeSelectContainer instanceof HTMLElement) {
      this.setStoreSelectContainer(storeSelectContainer)
    }

    const storeAddressInput = this.querySelector('[data-component-click-and-collect-store-address-input]')
    if (storeAddressInput instanceof HTMLInputElement) {
      this.setStoreAddressInput(storeAddressInput)
    }

    const productDeliveryOptionsCustom = document.querySelector('product-delivery-options-custom')
    if (productDeliveryOptionsCustom) {
      this.setProductDeliveryOptionsCustom(productDeliveryOptionsCustom)
    }

    const inStoreAccordion = document.querySelector('in-store-accordion')
    if (inStoreAccordion) {
      this.setInStoreAccordion(inStoreAccordion)
    }
    const productFormCustom = document.querySelector('product-form-custom')
    if (productFormCustom) {
      this.setProductFormCustom(productFormCustom)
    }
  }

  /**
   * Takes an array of retail locations and creates the store select elements for each one,
   * then fills them with the store options. Automatically selects the input that matches this.selectedStoreId
   * or this.selectedStoreInputIndex.
   * @param {RetailLocation[]} retailLocations - An array of retail locations to create store select elements for.
   */
  createStoreSelectElements(retailLocations) {
    this.storeSelectContainer.innerHTML = ''
    this.dateSelectElement.innerHTML = ''
    const iconImageUrl = this.dataset.iconImageUrl

    retailLocations.forEach((retailLocation, index) => {
      const { Name, Address, Opening_Dates, id } = retailLocation

      const input = frel('input', {
        type: 'radio',
        name: 'properties[Location]',
        value: Name,
        id: `store-${id}`,
        hidden: true,
        'data-opening-dates': JSON.stringify(Opening_Dates),
        'data-retail-location-id': id,
        'data-store-address': Address,
      })

      if (this.selectedStoreId !== null && id === this.selectedStoreId) {
        input.checked = true
      } else if (index === this.selectedStoreInputIndex) {
        input.checked = true
      }

      const label = frel(
        'label',
        {
          for: `store-${id}`,
          class: 'pcc__store-label',
        },
        frel(
          'div',
          { class: 'pcc__store-label-icon' },
          frel('img', { src: iconImageUrl, alt: 'Store icon', width: 24, height: 24 })
        ),
        frel(
          'div',
          { class: 'pcc__store-label-details' },
          frel('div', { class: 'pcc__store-name' }, Name),
          frel('div', { class: 'pcc__store-address' }, Address)
        )
      )

      this.storeSelectContainer.append(input, label)

      input.addEventListener('change', () => {
        if (input.checked) {
          const dates = JSON.parse(input.dataset.openingDates)
          this.createDateOptionElements(dates)
          this.updateStoreAddress(input.dataset.storeAddress)
        }
      })
    })

    if (retailLocations.length > 0) {
      const selectedInput =
        this.storeSelectContainer.querySelector(`input[data-retail-location-id="${this.selectedStoreId}"]`) ||
        this.storeSelectContainer.querySelectorAll('input[type="radio"]')[this.selectedStoreInputIndex]

      if (selectedInput) {
        selectedInput.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
  }

  /**
   * Creates option elements for each date and appends them to the pickup date select element.
   * @param {string[]} dates - An array of dates to create option elements for.
   */
  createDateOptionElements(dates) {
    const data = [
      { text: 'Select a date', value: '', placeholder: true },
      ...dates.map((date) => ({ text: date, value: date })),
    ]

    this.dateSlimSelect?.setData(data)

    this.dateSlimSelect?.setSelected('')
  }
}

customElements.define('product-click-and-collect-custom', ProductClickAndCollectCustom)
